import { useAiAnswersPageContext } from "@contexts/main/ai_answers_page_context"
import {
  listAiConversationsQuery,
  listAiMessageStepsQuery
} from "@domain/ai_answers"
import { Box } from "@mui/material"
import AiConversation, {
  ConversationThread
} from "@pages/main/ai_answers/sub_components/AiConversation"
import AiMessageList from "@pages/main/ai_answers/sub_components/AiMessageList"
import AiStepsDrawer from "@pages/main/ai_answers/sub_components/AiStepsDrawer"
import useAiConversationMessages from "@pages/main/ai_answers/useAiConversationMessages"
import useAiMessagesRealtime from "@pages/main/ai_answers/useAiMessagesRealtime"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { isValidConversationId } from "@utils/ai_conversation_id"
import { showErrorToast } from "@utils/toast"
import { useEffect, useMemo, useState } from "react"
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom"
import {
  AiMessages,
  AiMessageStatusEnum,
  AiMessageSteps,
  ChannelType
} from "__generated__/graphql"

const RUN_PARAM = "run"
const FAILED_PARAM = "failed"
const RUN_OPENED_HERE = "aiLogsRunOpenedHere"

function isRunOpenedHere(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as Record<string, unknown>)[RUN_OPENED_HERE] === true
  )
}

export default function AiAnswersPage() {
  const { selectedCustomer, translate } = useAiAnswersPageContext()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [selectedItem, setSelectedItem] = useState<AiMessages | null>(null)
  const conversation = useAiConversationMessages(selectedItem)
  const { messages, hasNextPage, isFetchingNextPage, fetchNextPage } =
    conversation
  const { conversationId: routeConversationId } = useParams<{
    conversationId?: string
  }>()

  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const runParam = searchParams.get(RUN_PARAM)
  const messageId =
    runParam && isValidConversationId(runParam) ? runParam : null
  const inspectFailedOnly = searchParams.get(FAILED_PARAM) === "1"
  const [lastInspectedId, setLastInspectedId] = useState<string | null>(null)
  const [pendingOlder, setPendingOlder] = useState<{
    from: string
    loadedCount: number
  } | null>(null)

  useAiMessagesRealtime((updatedMessage) => {
    if (updatedMessage._id === messageId) {
      void queryClient.invalidateQueries({
        queryKey: ["customer-data-table", messageId]
      })
    }
  })

  const { data: steps, isLoading: stepsLoading } = useQuery({
    queryKey: ["customer-data-table", messageId],
    queryFn: async () => {
      if (!messageId) return []
      const response = await listAiMessageStepsQuery(messageId)
      if (!response.isSuccess || !response.data) {
        showErrorToast(response.errorMessage ?? "Bir hata oluştu")
        return []
      }
      return response.data as unknown as AiMessageSteps[]
    },
    enabled: !!messageId
  })

  const needsDeepLinkFetch = Boolean(
    routeConversationId &&
    isValidConversationId(routeConversationId) &&
    selectedItem?.conversationId?._id !== routeConversationId
  )

  const {
    data: deepLinkConversation,
    isFetching: isDeepLinkFetching,
    isError: isDeepLinkError
  } = useQuery({
    queryKey: ["ai-conversation-by-id", routeConversationId],
    queryFn: async () => {
      const response = await listAiConversationsQuery({
        conversationId: routeConversationId,
        limit: 1,
        offset: 0
      })
      if (!response.isSuccess || !response.data) {
        throw new Error(response.errorMessage ?? "Bir hata oluştu")
      }
      return response.data
    },
    enabled: needsDeepLinkFetch,
    retry: false
  })

  useEffect(() => {
    if (!routeConversationId) return

    if (!isValidConversationId(routeConversationId)) {
      showErrorToast(
        translate("ai_logs_invalid_conversation_id") || "Geçersiz sohbet ID"
      )
      navigate("/ai/logs", { replace: true })
      return
    }

    if (!needsDeepLinkFetch || isDeepLinkFetching) return

    const doc = deepLinkConversation?.docs?.[0]
    if (isDeepLinkError || !doc?._id) {
      showErrorToast(
        translate("ai_logs_conversation_not_found") || "Sohbet bulunamadı"
      )
      navigate("/ai/logs", { replace: true })
      return
    }

    setSelectedItem({
      _id: doc._id,
      customerId: doc.customerId ?? "",
      conversationId: doc.conversationId ?? null,
      answerId: doc.answerId ?? "",
      questionId: doc.questionId ?? "",
      question: doc.question ?? "",
      answer: doc.answer ?? "",
      status: doc.status ?? AiMessageStatusEnum.Failed,
      channelType: doc.channelType as ChannelType | null,
      error: doc.error ?? null,
      createdAt: doc.createdAt ?? new Date(),
      updatedAt: doc.updatedAt ?? new Date()
    })
  }, [
    routeConversationId,
    needsDeepLinkFetch,
    isDeepLinkFetching,
    isDeepLinkError,
    deepLinkConversation,
    navigate,
    translate
  ])

  const handleSelect = (item: AiMessages) => {
    const convId = item.conversationId?._id
    navigate(convId ? `/ai/logs/${convId}` : "/ai/logs", { replace: true })
    setSelectedItem(item)
  }

  // Opening pushes a history entry so the browser's back button closes the panel; stepping between
  // runs replaces it, so back never walks through every run you looked at.
  const openRun = (runId: string, options?: { onlyFailed?: boolean }) => {
    const next = new URLSearchParams(searchParams)
    next.set(RUN_PARAM, runId)
    if (options?.onlyFailed) next.set(FAILED_PARAM, "1")
    else next.delete(FAILED_PARAM)
    const alreadyOpen = !!messageId
    setSearchParams(next, {
      replace: alreadyOpen,
      state: alreadyOpen ? location.state : { [RUN_OPENED_HERE]: true }
    })
  }

  const closeRun = () => {
    if (isRunOpenedHere(location.state)) {
      navigate(-1)
      return
    }
    const next = new URLSearchParams(searchParams)
    next.delete(RUN_PARAM)
    next.delete(FAILED_PARAM)
    setSearchParams(next, { replace: true })
  }

  const handleInspectSteps = (
    message: AiMessages,
    options?: { onlyFailed?: boolean }
  ) => openRun(message._id, options)

  useEffect(() => {
    if (messageId) setLastInspectedId(messageId)
  }, [messageId])

  const inspectedMessage =
    messages.find((message) => message._id === messageId) ?? null
  const inspectedIndex = inspectedMessage
    ? messages.indexOf(inspectedMessage)
    : -1

  const goToOlderRun = () => {
    if (inspectedIndex > 0) {
      openRun(messages[inspectedIndex - 1]._id)
      return
    }
    if (inspectedIndex === 0 && hasNextPage && messageId) {
      setPendingOlder({ from: messageId, loadedCount: messages.length })
      void fetchNextPage()
    }
  }

  useEffect(() => {
    if (!pendingOlder) return
    const loadedMore = messages.length > pendingOlder.loadedCount
    if (!loadedMore && (isFetchingNextPage || hasNextPage)) return
    setPendingOlder(null)
    const index = messages.findIndex((m) => m._id === pendingOlder.from)
    if (index > 0) openRun(messages[index - 1]._id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOlder, isFetchingNextPage, hasNextPage, messages])

  const runNavigation =
    inspectedIndex === -1
      ? undefined
      : {
          position: inspectedIndex + 1,
          total: messages.length,
          hasOlderUnloaded: !!hasNextPage,
          onPrev: inspectedIndex > 0 || hasNextPage ? goToOlderRun : undefined,
          onNext:
            inspectedIndex < messages.length - 1
              ? () => openRun(messages[inspectedIndex + 1]._id)
              : undefined
        }

  const thread: ConversationThread | null = useMemo(() => {
    if (!selectedItem) return null

    return {
      id: selectedItem._id,
      customerName: selectedCustomer?.customer?.companyName || "",
      channel: selectedItem.channelType || ""
    }
  }, [selectedItem, selectedCustomer])

  return (
    <Box sx={{ height: "100vh", overflow: "hidden", display: "flex" }}>
      <AiMessageList onSelect={handleSelect} selectedItem={selectedItem} />
      {/* The steps panel covers the conversation instead of squeezing it: side by side, a step log
          with its JSON and skill timeline had no room left to be read. It overlays rather than
          replaces so the conversation keeps its scroll position and paged history underneath —
          closing the panel returns you to the turn you were reading, not to the newest message. */}
      <Box sx={{ flex: 1, minWidth: 0, position: "relative", display: "flex" }}>
        <AiConversation
          thread={thread}
          conversation={conversation}
          onInspectSteps={handleInspectSteps}
          selectedItem={selectedItem}
          activeMessageId={messageId}
          lastInspectedId={lastInspectedId}
        />
        {messageId && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 4,
              display: "flex",
              boxShadow: 3
            }}
          >
            <AiStepsDrawer
              key={messageId}
              open
              onClose={closeRun}
              steps={steps}
              isLoading={stepsLoading}
              runId={messageId}
              message={inspectedMessage}
              backLabel={translate("ai_logs_back_to_conversation")}
              initialOnlyFailed={inspectFailedOnly}
              navigation={runNavigation}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}
